import type { SchemaSite } from '@/utils/schema-site';
import { getSchemaSite } from '@/lib/schema-site.server';
import { JsonLd } from '@/components/json-ld';
import { generateWebsiteSchema } from '@/utils/content-schema';
import logo from '/images/logo.svg?no-inline';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
    type LoaderFunctionArgs,
    type MetaDescriptor,
    redirect,
    type ShouldRevalidateFunctionArgs,
} from 'react-router';
import type { Route } from './+types/_app._index';
import { resolvePrefix } from '@salesforce/storefront-next-runtime/site-context';
import { Region } from '@/components/region';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/util/pageLoader.server';
import { getLogger } from '@/lib/logger.server';
import { readPrimaryShoppingCategoryPath, readPrimaryShoppingPillar } from '@/lib/primary-shopping-category.server';
import { buildUrlFromContext } from '@/lib/url.server';
import {
    NEWSLETTER_CONFIRM_STATUS_PARAM,
    readNewsletterConfirmStatus,
    type NewsletterConfirmStatus,
} from '@/lib/newsletter-confirm';
import {
    PROFILE_UPDATE_CONFIRM_STATUS_PARAM,
    readProfileUpdateConfirmStatus,
    type ProfileUpdateConfirmStatus,
} from '@/lib/profile-update-confirm';
import {
    REGISTRATION_CONFIRM_STATUS_PARAM,
    readRegistrationConfirmStatus,
    type RegistrationConfirmStatus,
} from '@/lib/registration-confirm';

import { DEFAULT_SITE_NAME_KEY } from '@/components/seo-meta';
import { resolvePageSeo } from '@/lib/page-seo';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';

import { shouldSkipRevalidationForCartOrWishlistAndNewsletterActions } from '@/lib/should-revalidate';
import { getPublicOrigin } from 'src/utils/schema-url';
import { attachAnalytics } from '@/lib/analytics/route-analytics';
import { resolvePillarHomeContentGroup } from '@/lib/analytics/content-group';
import hero01 from '/images/eataly-og-default.jpg';

const NewsletterConfirmDialog = lazy(() => import('@/components/newsletter/newsletter-confirm-dialog'));
const ProfileUpdateConfirmDialog = lazy(() => import('@/components/profile/profile-update-confirm-dialog'));
const RegistrationConfirmDialog = lazy(() => import('@/components/registration/registration-confirm-dialog'));

@PageType({
    name: 'Home Page',
    description: 'Main landing page with hero carousel, featured products, and help sections',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'headerbanner',
        name: 'Hero Modules',
        description: 'Region for Hero modules',
        maxComponents: 1,
        componentTypeInclusions: ['content.heroModules'],
    },
    {
        id: 'main',
        name: 'Main Content Region',
        description: 'Region for main content',
        maxComponents: 2,
        componentTypeInclusions: ['content.seoText', 'carousels.squareCardCarousel'],
    },
])
export class HomePageMetadata {}

export type HomePageData = {
    schemaSite?: SchemaSite;
    page: Awaited<ReturnType<typeof fetchPageWithComponentData>>;
    pageUrl: string;
    ogImageUrl: string;
    seoTitle: string;
    seoDescription: string | undefined;
    seoRawTitle: boolean;
    seoSiteName: string;
    newsletterConfirm: NewsletterConfirmStatus | null;
    profileUpdateConfirm: ProfileUpdateConfirmStatus | null;
    registrationConfirm: RegistrationConfirmStatus | null;
};

export const meta: Route.MetaFunction = ({ loaderData }) => {
    if (!loaderData) return [];

    const { seoTitle, seoDescription, seoRawTitle, pageUrl, ogImageUrl, seoSiteName } = loaderData;
    const documentTitle = seoRawTitle ? seoTitle : `${seoTitle} | ${seoSiteName}`;
    const socialTitle = seoTitle || seoSiteName;
    const twitterCard = ogImageUrl ? 'summary_large_image' : 'summary';

    const descriptors: MetaDescriptor[] = [
        { title: documentTitle },
        { name: 'robots', content: 'index, follow' },
        { property: 'og:title', content: socialTitle },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: seoSiteName },
        { name: 'twitter:card', content: twitterCard },
        { name: 'twitter:title', content: socialTitle },
    ];

    if (seoDescription) {
        descriptors.push(
            { name: 'description', content: seoDescription },
            { property: 'og:description', content: seoDescription },
            { name: 'twitter:description', content: seoDescription }
        );
    }

    if (pageUrl) {
        descriptors.push({ property: 'og:url', content: pageUrl });
    }
    if (ogImageUrl) {
        descriptors.push({ property: 'og:image', content: ogImageUrl }, { name: 'twitter:image', content: ogImageUrl });
    }

    return descriptors;
};

/**
 * Server-side loader function that fetches home page data.
 * This function runs on the server during SSR and prepares data for the home page.
 * @returns Promise that resolves to the Page Designer and SEO data consumed by the home page
 */
export async function loader(args: LoaderFunctionArgs): Promise<HomePageData> {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);
    const newsletterConfirm = readNewsletterConfirmStatus(requestUrl.searchParams);
    const profileUpdateConfirm = readProfileUpdateConfirmStatus(requestUrl.searchParams);
    const registrationConfirm = readRegistrationConfirmStatus(requestUrl.searchParams);

    // Redirect bare "/" to the default site/locale prefixed homepage
    if (requestUrl.pathname === '/' && config.url?.prefix && config.url.prefix !== '/') {
        const siteRef = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const defaultSite = config.commerce.sites.find((s) => s.id === config.defaultSiteId);
        const defaultLocale = defaultSite?.defaultLocale ?? config.i18n.fallbackLng;
        const localeRef = config.localeAliasMap?.[defaultLocale] ?? defaultLocale;
        const prefixedPath = resolvePrefix({
            prefix: config.url.prefix,
            params: { siteId: siteRef, localeId: localeRef },
        });
        throw redirect(`${prefixedPath}/${requestUrl.search}`);
    }

    const primaryCategoryPath = await readPrimaryShoppingCategoryPath(args.request, args.context);
    if (primaryCategoryPath && !newsletterConfirm && !profileUpdateConfirm && !registrationConfirm) {
        throw redirect(buildUrlFromContext(primaryCategoryPath, args.context));
    }

    const publicOrigin = getPublicOrigin(args.request);
    const pageUrl = buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search);

    const page = await fetchPageWithComponentData(
        args,
        {
            pageId: 'homepage',
        },
        { criticalRegions: ['headerbanner'] }
    );
    const primaryShoppingPillar = await readPrimaryShoppingPillar(args.request, args.context);

    const { t } = getTranslation(args.context);
    const seoSiteName = t(DEFAULT_SITE_NAME_KEY, { ns: 'common' });
    const fallbackTitle = t('meta.title', { ns: 'home', defaultValue: 'NextGen PWA Kit Store' });
    const fallbackDescription = t('meta.description', {
        ns: 'home',
        defaultValue: 'Welcome to our web store for high performers!',
    });
    const {
        title,
        description,
        rawTitle: seoRawTitle,
    } = resolvePageSeo({
        pageTitle: page?.pageTitle,
        pageDescription: page?.pageDescription,
        title: fallbackTitle,
        description: fallbackDescription,
        rawTitle: true,
    });
    const seoTitle = title ?? fallbackTitle;
    const seoDescription = description;

    return attachAnalytics(
        {
            page,
            schemaSite: getSchemaSite(args),
            pageUrl,
            ogImageUrl: new URL(hero01, publicOrigin).href,
            seoTitle,
            seoDescription,
            seoRawTitle,
            seoSiteName,
            newsletterConfirm,
            profileUpdateConfirm,
            registrationConfirm,
        },
        resolvePillarHomeContentGroup(primaryShoppingPillar)
    );
}

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
    if (shouldSkipRevalidationForCartOrWishlistAndNewsletterActions(args)) {
        return false;
    }

    return args.defaultShouldRevalidate;
}

/**
 * Home page component that displays the home page content with granular Suspense boundaries.
 * Components within the page handle their own Suspense boundaries for progressive loading.
 * @returns JSX element representing the home page layout
 */
function NewsletterConfirmOverlay({ status }: { status: NewsletterConfirmStatus }) {
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setLoaded(true);
        setOpen(true);
    }, []);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            const url = new URL(window.location.href);
            url.searchParams.delete(NEWSLETTER_CONFIRM_STATUS_PARAM);
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }
    }, []);

    if (!loaded) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <NewsletterConfirmDialog status={status} open={open} onOpenChange={handleOpenChange} />
        </Suspense>
    );
}

function RegistrationConfirmOverlay({ status }: { status: RegistrationConfirmStatus }) {
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setLoaded(true);
        setOpen(true);
    }, []);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            const url = new URL(window.location.href);
            url.searchParams.delete(REGISTRATION_CONFIRM_STATUS_PARAM);
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }
    }, []);

    if (!loaded) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <RegistrationConfirmDialog status={status} open={open} onOpenChange={handleOpenChange} />
        </Suspense>
    );
}

function ProfileUpdateConfirmOverlay({ status }: { status: ProfileUpdateConfirmStatus }) {
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setLoaded(true);
        setOpen(true);
    }, []);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            const url = new URL(window.location.href);
            url.searchParams.delete(PROFILE_UPDATE_CONFIRM_STATUS_PARAM);
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }
    }, []);

    if (!loaded) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <ProfileUpdateConfirmDialog status={status} open={open} onOpenChange={handleOpenChange} />
        </Suspense>
    );
}

export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    return (
        <div>
            {loaderData.pageUrl && (
                <JsonLd
                    id="website-schema"
                    data={generateWebsiteSchema(
                        loaderData.schemaSite?.homeUrl ?? loaderData.pageUrl,
                        loaderData.seoSiteName,
                        new URL(logo, loaderData.pageUrl).href,
                        loaderData.schemaSite
                    )}
                />
            )}
            {/* Header Banner Region - Region component handles its own Suspense internally */}
            <div>
                <Region page={loaderData.page} regionId="headerbanner" fallbackElement={<></>} errorElement={<></>} />
            </div>

            {/* Main Region - Region component handles its own Suspense internally */}
            {/* Note: This region doesn't provide fallback skeletons right now as it's located below the fold */}
            <Region page={loaderData.page} regionId="main" errorElement={<></>} />
            {loaderData.newsletterConfirm ? <NewsletterConfirmOverlay status={loaderData.newsletterConfirm} /> : null}
            {loaderData.profileUpdateConfirm ? (
                <ProfileUpdateConfirmOverlay status={loaderData.profileUpdateConfirm} />
            ) : null}
            {loaderData.registrationConfirm ? (
                <RegistrationConfirmOverlay status={loaderData.registrationConfirm} />
            ) : null}
        </div>
    );
}
